import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  BookOpen, 
  Filter, 
  Search, 
  Tag,
  Clock,
  BarChart,
  Globe,
  Code,
  Database,
  Layers
} from 'lucide-react';

// Sample course data with categories and metadata
const COURSE_CATEGORIES = [
  {
    id: 'frontend',
    name: 'Frontend Development',
    icon: Globe,
    color: 'bg-blue-100 text-blue-800',
    courses: [
      { id: 'html-css-fundamentals', title: 'HTML & CSS Fundamentals', level: 'foundation', duration: '3-4 weeks' },
      { id: 'javascript-basics', title: 'JavaScript Basics', level: 'foundation', duration: '4-5 weeks' },
      { id: 'react-basics', title: 'React Fundamentals', level: 'intermediate', duration: '6-8 weeks' },
      { id: 'vue-js-essentials', title: 'Vue.js Essentials', level: 'intermediate', duration: '5-6 weeks' }
    ]
  },
  {
    id: 'backend',
    name: 'Backend Development',
    icon: Database,
    color: 'bg-green-100 text-green-800',
    courses: [
      { id: 'sql-fundamentals', title: 'SQL Fundamentals', level: 'foundation', duration: '4-5 weeks' },
      { id: 'node-js-basics', title: 'Node.js Basics', level: 'intermediate', duration: '6-7 weeks' },
      { id: 'python-programming', title: 'Python Programming', level: 'intermediate', duration: '8-10 weeks' },
      { id: 'api-development', title: 'API Development', level: 'advanced', duration: '6-8 weeks' }
    ]
  },
  {
    id: 'foundation',
    name: 'Programming Foundation',
    icon: Code,
    color: 'bg-purple-100 text-purple-800',
    courses: [
      { id: 'programming-basics', title: 'Programming Fundamentals', level: 'foundation', duration: '4-5 weeks' },
      { id: 'git-version-control', title: 'Git & Version Control', level: 'foundation', duration: '2-3 weeks' },
      { id: 'http-rest-concepts', title: 'HTTP & REST API Concepts', level: 'foundation', duration: '2-3 weeks' }
    ]
  },
  {
    id: 'fullstack',
    name: 'Full Stack Development',
    icon: Layers,
    color: 'bg-orange-100 text-orange-800',
    courses: [
      { id: 'fullstack-fundamentals', title: 'Full Stack Fundamentals', level: 'intermediate', duration: '12-16 weeks' },
      { id: 'mern-stack', title: 'MERN Stack Development', level: 'advanced', duration: '16-20 weeks' },
      { id: 'deployment-devops', title: 'Deployment & DevOps', level: 'advanced', duration: '4-6 weeks' }
    ]
  }
];

const CourseCategorizationFilters: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const getAllCourses = () => {
    return COURSE_CATEGORIES.flatMap(category => 
      category.courses.map(course => ({
        ...course,
        category: category.name,
        categoryId: category.id,
        categoryColor: category.color
      }))
    );
  };

  const filteredCourses = getAllCourses().filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.categoryId === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    return matchesSearch && matchesCategory && matchesLevel;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.title.localeCompare(b.title);
      case 'level':
        const levelOrder = { foundation: 1, intermediate: 2, advanced: 3 };
        return levelOrder[a.level as keyof typeof levelOrder] - levelOrder[b.level as keyof typeof levelOrder];
      case 'duration':
        return a.duration.localeCompare(b.duration);
      default:
        return 0;
    }
  });

  const getLevelBadgeVariant = (level: string) => {
    switch (level) {
      case 'foundation':
        return 'default';
      case 'intermediate':
        return 'secondary';
      case 'advanced':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Course Organization & Categories</h3>
          <p className="text-sm text-muted-foreground">
            Browse and filter courses by category, level, and other criteria
          </p>
        </div>
      </div>

      {/* Category Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COURSE_CATEGORIES.map((category) => {
          const IconComponent = category.icon;
          return (
            <Card 
              key={category.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedCategory === category.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedCategory(selectedCategory === category.id ? 'all' : category.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded ${category.color}`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <h4 className="font-medium text-sm">{category.name}</h4>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    {category.courses.length} courses
                  </p>
                  <div className="flex gap-1">
                    {Array.from(new Set(category.courses.map(c => c.level))).map(level => (
                      <Badge key={level} variant="outline" className="text-xs">
                        {level}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Course Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Courses</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by course name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {COURSE_CATEGORIES.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Level</Label>
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="foundation">Foundation</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Sort By</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Course Name</SelectItem>
                  <SelectItem value="level">Difficulty Level</SelectItem>
                  <SelectItem value="duration">Duration</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Course Results
            </div>
            <Badge variant="outline">
              {filteredCourses.length} courses found
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {filteredCourses.map((course) => (
              <Card key={course.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <BookOpen className="h-8 w-8 text-primary" />
                      <div>
                        <h4 className="font-semibold">{course.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {course.category}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{course.duration}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={getLevelBadgeVariant(course.level) as any}
                        className="capitalize"
                      >
                        {course.level}
                      </Badge>
                      <Badge className={course.categoryColor}>
                        <Tag className="h-3 w-3 mr-1" />
                        {course.category}
                      </Badge>
                      <Button size="sm" variant="outline">
                        Quick Assign
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {filteredCourses.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No courses found</h3>
                <p>Try adjusting your search criteria or filters</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseCategorizationFilters;